import type { ApiClient } from './client';

export interface AvatarCard {
  id: number;
  resource_kind: string;
  name: string;
  full_name: string;
  source: string;
  image_url: string;
  /** Превью портрета для списков (192 px); нет — списки берут image_url */
  image_thumb_url?: string | null;
  sex: string | null;
  short_description: string;
  voice_ids: number[];
  default_voice_id: number | null;
  skins: Array<{ id: number; name: string; image_url: string }>;
  collection_ids: number[];
  /** Подборки голосов с этим персонажем (docs/03 §4.5): раздел «Персонажи» открытой подборки «Озвучки»; старый сервер поле не присылает */
  voice_collection_ids?: number[];
  is_new: boolean;
  popularity: number;
  version: number;
  /** Выдуманный персонаж (true) или реальный человек — актёр, диктор (false), D-270; у вариантов «Вы» и у старого сервера нет */
  is_fiction?: boolean | null;
}

export interface AvatarVoice {
  id: number;
  name: string;
  avatar_url: string;
  /** Превью аватарки 128 px (BFF); нет — avatar_url */
  avatar_thumb_url?: string | null;
  preview_url: string;
  can_tts: boolean;
  can_s2s: boolean;
}

export interface AvatarCatalog {
  avatars: AvatarCard[];
  collections: Array<{ id: number | null; system_code: string | null;
    name: string; description: string; cover_url: string | null;
    avatar_count: number; avatar_ids: number[] }>;
  recommended_ids: number[];
  default_voices: AvatarVoice[];
  catalog_version: string;
}

export interface AvatarDetails {
  avatar: AvatarCard;
  long_description: string;
  role: string;
  temper: string;
  voices: AvatarVoice[];
  /** Голоса персонажа строками каталога (имя на языке профиля, теги, пол, превью аватарки): карточка встаёт одним запросом.
      Нет поля или null — старый бэкенд или сбой сервиса голосов: голоса догружаются по одному (GET /app/catalog/voices/{id}) */
  voice_cards?: import('./types').Voice[] | null;
}

export interface SfxPresets {
  scope: 'avatar' | 'platform';
  items: Array<{ id: number; avatar_id: number; label: string; prompt: string }>;
  catalog_version: string;
}

/** Реальный человек каталога (D-270): только вкладка «Актёры» «Озвучки»; отсутствие поля — выдуманный персонаж */
export const isActor = (card: Pick<AvatarCard, 'is_fiction'>) => card.is_fiction === false;

/** Каталог для рядов лиц, ролей и каталога персонажей экранов: без реальных людей (D-270) — ни в списке, ни в подборках */
export function faceCatalog(catalog: AvatarCatalog): AvatarCatalog {
  const actors = new Set(catalog.avatars.filter(isActor).map(card => card.id));
  if (!actors.size) return catalog;
  const keep = (id: number) => !actors.has(id);
  return {
    ...catalog,
    avatars: catalog.avatars.filter(card => keep(card.id)),
    collections: catalog.collections
      .map(c => ({ ...c, avatar_ids: c.avatar_ids.filter(keep), avatar_count: c.avatar_ids.filter(keep).length }))
      .filter(c => c.avatar_count > 0),
    recommended_ids: catalog.recommended_ids.filter(keep),
  };
}

export const avatarCatalog = (client: ApiClient) => ({
  image: (id: number, signal?: AbortSignal) => client.download(`/app/catalog/avatars/${id}/image`, signal),
  list: (locale: 'ru' | 'en' = 'ru', signal?: AbortSignal) =>
    client.request<AvatarCatalog>('/app/catalog/avatars', { query: { locale }, signal }),
  details: (id: number, locale: 'ru' | 'en' = 'ru', signal?: AbortSignal) =>
    client.request<AvatarDetails>(`/app/catalog/avatars/${id}`, { query: { locale }, signal }),
  sfx: (avatarId?: number, locale: 'ru' | 'en' = 'ru', signal?: AbortSignal) =>
    client.request<SfxPresets>('/app/sfx-presets', {
      query: { avatar_id: avatarId, locale }, signal,
    }),
});
